/**
 * Пункты меню из config.json мероприятия.
 *
 * Боковое меню и плитки дашборда строятся из `apps`, нижняя панель — из
 * `fast_apps`. Объекты в обоих списках одинаковые:
 *
 *   { moduleName, iconName, alias?, hash?, anchor_ru, anchor_en,
 *     isDisabled?, isVisible? }
 *
 * `moduleName` — имя маршрута из router/index.js.
 *
 * Не миксин, обычный модуль с функциями — как profileRubrics.js рядом.
 */

const POINT_PROPS = ['moduleName', 'iconName', 'alias', 'hash', 'isDisabled', 'isVisible'];

// Больше четырёх кнопок нижняя панель не вмещает. Лишние сервер присылать
// может — отсекаются здесь, см. pickBottomNavPoints.
export const BOTTOM_NAV_LIMIT = 4;

// Пункт узнаётся не по одному moduleName: staticPage в конфиге встречается
// несколько раз с разными alias, а FAQ — ещё и с якорем на вопрос спикеру.
const pointKey = (item) => `${item.moduleName}|${item.alias || ''}|${item.hash || ''}`;

/**
 * Объект из конфига → пункт меню с подписью на текущем языке.
 */
export function toMenuPoint(item, locale) {
  const point = {};
  POINT_PROPS.forEach((propName) => {
    if (item[propName] !== undefined) point[propName] = item[propName];
  });
  if (item.hash !== undefined) point.class = 'no-active-light';
  point.anchor = '';
  if (item.anchor_ru !== undefined && locale === 'ru') point.anchor = item.anchor_ru;
  if (item.anchor_en !== undefined && locale === 'en') point.anchor = item.anchor_en;
  return point;
}

/**
 * Ссылка на пункт — как у плитки меню (asideMenuItem.vue).
 */
export function pointRoute(point) {
  return {
    name: point.moduleName,
    hash: point.hash || '',
    params: point.alias ? { alias: point.alias } : {},
  };
}

const isFilledString = (value) => typeof value === 'string' && value !== '';

// Ссылку роутер собрать может: маршрут в ветке есть (модуль могли вырезать
// из клиентской ветки, а сервер о том не знает) и обязательные параметры на
// месте — staticPage без alias роняет router-link, а с ним всю панель.
const canOpen = (router, item) => {
  try {
    router.resolve(pointRoute(item));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Состав нижней панели из `fast_apps`.
 *
 * Возвращает null только когда ключа в конфиге нет: сервер старый и составом
 * панели не управляет — тогда панель остаётся той, что зашита в разметке
 * BottomNav. Всё остальное, что не годится в пункт, считается ничем: не
 * массив — пустой состав, негодный элемент выбрасывается. Пустой состав
 * панель прячет, кнопок по умолчанию на этот случай нет.
 *
 * Погашенность берётся из `apps`: fast_apps её не дублирует. Из самого
 * fast_apps isDisabled читается только у пунктов, которых в боковом меню нет.
 *
 * @param {*} fastApps — config.fast_apps как пришёл
 * @param {Array} apps — config.apps
 * @param {Object} router — $router ветки
 */
export function pickBottomNavPoints(fastApps, apps, router) {
  if (fastApps === undefined) return null;
  if (!Array.isArray(fastApps)) return [];

  const disabledInApps = new Map((apps || []).map((item) => [
    pointKey(item), item.isDisabled === true,
  ]));

  const points = fastApps
    // Пункт панели — это иконка, без неё кнопка пустая
    .filter((item) => item && typeof item === 'object'
      && isFilledString(item.moduleName) && isFilledString(item.iconName))
    .filter((item) => item.isVisible !== false && canOpen(router, item))
    .map((item) => {
      const key = pointKey(item);
      const isDisabled = disabledInApps.has(key)
        ? disabledInApps.get(key)
        : item.isDisabled === true;
      return { ...item, isDisabled };
    });

  // Не влезают — первыми уходят погашенные, дальше по порядку сервера
  const pool = points.length > BOTTOM_NAV_LIMIT
    ? points.filter((item) => !item.isDisabled)
    : points;

  return pool.slice(0, BOTTOM_NAV_LIMIT);
}
